import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  LiveKitRoom,
  VideoConference,
  RoomAudioRenderer,
} from "@livekit/components-react";
import "@livekit/components-styles";
import { useAuth } from "../auth/AuthProvider";
import { fetchLessonToken } from "../lib/livekit";
import { Whiteboard } from "../components/Whiteboard";

type Tab = "video" | "whiteboard";

export function RoomPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("video");

  useEffect(() => {
    if (!ready || !user || !id) return;
    let cancelled = false;
    fetchLessonToken(id)
      .then((r) => {
        if (cancelled) return;
        setToken(r.token);
        setServerUrl(r.url);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, [ready, user, id]);

  if (!ready) {
    return (
      <div className="h-full flex items-center justify-center bg-ink-900 text-ink-400">
        Wczytywanie…
      </div>
    );
  }

  if (!user) {
    return (
      <div className="h-full flex items-center justify-center bg-ink-900 text-white p-6">
        Musisz być zalogowany. <Link to="/" className="ml-2 underline">Wróć</Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-ink-900 p-6">
        <div className="bg-red-900/60 border border-red-700 text-red-100 rounded-xl px-4 py-3 max-w-md">
          Nie udało się dołączyć: {error}
          <button onClick={() => navigate("/")} className="block mt-3 text-sm underline">
            Wróć
          </button>
        </div>
      </div>
    );
  }

  if (!token || !serverUrl) {
    return (
      <div className="h-full flex items-center justify-center bg-ink-900 text-ink-400">
        Łączenie z lekcją…
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-lk-theme="default">
      <LiveKitRoom
        token={token}
        serverUrl={serverUrl}
        connect
        video
        audio
        onDisconnected={() => navigate(`/lessons/${id}`)}
        style={{ height: "100%", display: "flex", flexDirection: "column" }}
      >
        <div className="flex items-center gap-2 px-4 py-2 bg-ink-900 border-b border-ink-700">
          <Link to={`/lessons/${id}`} className="text-xs text-ink-400 hover:text-white">
            ← Lekcja
          </Link>
          <div className="flex-1" />
          <button
            onClick={() => setTab("video")}
            className={`text-xs px-3 py-1.5 rounded-lg border ${
              tab === "video" ? "bg-lime-500 text-ink-900 border-lime-500" : "border-ink-600 text-ink-300"
            }`}
          >
            Wideo
          </button>
          <button
            onClick={() => setTab("whiteboard")}
            className={`text-xs px-3 py-1.5 rounded-lg border ${
              tab === "whiteboard" ? "bg-lime-500 text-ink-900 border-lime-500" : "border-ink-600 text-ink-300"
            }`}
          >
            Tablica
          </button>
        </div>
        <div className="flex-1 relative" style={{ minHeight: 0 }}>
          <div style={{ display: tab === "video" ? "block" : "none", height: "100%" }}>
            <VideoConference />
          </div>
          <div style={{ display: tab === "whiteboard" ? "block" : "none", height: "100%" }}>
            <Whiteboard />
          </div>
        </div>
        <RoomAudioRenderer />
      </LiveKitRoom>
    </div>
  );
}
